import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Save, X, Eye, EyeOff, LogOut, 
  Languages, Loader2, Image, Search, Upload, Tag,
  Bold, Italic, List, Link2, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BlogPostCard } from '@/components/admin/blog/BlogPostCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios
const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

const languages = [
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

const categories = ['Website Tips', 'SEO', 'Design', 'Marketing', 'Technologie', 'Business'];

const initialFormData = {
  title_nl: '', content_nl: '', excerpt_nl: '',
  title_en: '', content_en: '', excerpt_en: '',
  title_fr: '', content_fr: '', excerpt_fr: '',
  title_de: '', content_de: '', excerpt_de: '',
  image_url: '', category: '', tags: [], author: 'Netblitz', is_published: false,
};

const BlogAdminPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  
  // Data state
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translatingIds, setTranslatingIds] = useState(new Set());
  
  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [expandedLangs, setExpandedLangs] = useState({});
  const [tagInput, setTagInput] = useState('');
  
  // Image state
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [searchedImages, setSearchedImages] = useState([]);
  const [searchingImages, setSearchingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/api/blog?include_drafts=true');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/blog/${editingId}`, formData);
      } else {
        await api.post('/api/blog', formData);
      }
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      }
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title_nl: post.title_nl || '', content_nl: post.content_nl || '', excerpt_nl: post.excerpt_nl || '',
      title_en: post.title_en || '', content_en: post.content_en || '', excerpt_en: post.excerpt_en || '',
      title_fr: post.title_fr || '', content_fr: post.content_fr || '', excerpt_fr: post.excerpt_fr || '',
      title_de: post.title_de || '', content_de: post.content_de || '', excerpt_de: post.excerpt_de || '',
      image_url: post.image_url || '', category: post.category || '', 
      tags: post.tags || [], author: post.author || 'Netblitz', is_published: post.is_published || false,
    });
    setEditingId(post.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) {
      try {
        await api.delete(`/api/blog/${id}`);
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const togglePublished = async (post) => {
    try {
      await api.put(`/api/blog/${post.id}`, { is_published: !post.is_published });
      fetchPosts();
    } catch (error) {
      console.error('Error toggling post:', error);
    }
  };

  const translatePost = async (postId) => {
    setTranslatingIds(prev => new Set([...prev, postId]));
    try {
      await api.post(`/api/blog/${postId}/translate`);
      // Poll for completion
      let attempts = 0;
      const checkStatus = async () => {
        attempts++;
        const response = await api.get(`/api/blog/${postId}/translation-status`);
        if (response.data.all_translated || attempts >= 30) {
          setTranslatingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(postId);
            return newSet;
          });
          fetchPosts();
        } else {
          setTimeout(checkStatus, 2000);
        }
      };
      setTimeout(checkStatus, 3000);
    } catch (error) {
      console.error('Error translating post:', error);
      setTranslatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  const searchImages = async () => {
    if (!imageSearchQuery.trim()) return;
    setSearchingImages(true);
    try {
      const response = await api.get('/api/images/search', {
        params: { query: imageSearchQuery, count: 9 }
      });
      setSearchedImages(response.data.images);
    } catch (error) {
      console.error('Error searching images:', error);
    } finally {
      setSearchingImages(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Alleen JPEG, PNG, GIF of WebP afbeeldingen zijn toegestaan.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Afbeelding is te groot. Maximaal 5MB.');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/api/images/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: `${BACKEND_URL}${response.data.url}` }));
        setShowImageSearch(false);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Fout bij uploaden van afbeelding.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setShowAddForm(false);
    setExpandedLangs({});
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const insertMarkdown = (prefix, suffix = prefix) => {
    const textarea = document.querySelector('[data-testid="input-content-nl"]');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content_nl;
    const selected = text.substring(start, end);
    const newText = text.substring(0, start) + prefix + selected + suffix + text.substring(end);
    setFormData({ ...formData, content_nl: newText });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fc] pt-32 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#f8f9fc] pt-32 pb-20" data-testid="blog-admin-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0f172a]">Blog Beheer</h1>
            <p className="text-[#475569] mt-1">Welkom, {user} • Beheer je blog artikelen</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-accent flex items-center gap-2"
              data-testid="add-post-button"
            >
              <Plus size={20} />
              Nieuw artikel
            </button>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-300 transition-colors flex items-center gap-2"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              Uitloggen
            </button>
          </div>
        </div>

        {/* Add/Edit Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 mb-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#0f172a]">
                  {editingId ? 'Artikel bewerken' : 'Nieuw artikel'}
                </h2>
                <button onClick={resetForm} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X size={20} className="text-slate-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dutch (required) */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-[#0f172a] font-semibold">
                    <span className="text-xl">🇳🇱</span>
                    Nederlands (verplicht)
                  </div>
                  <input
                    type="text"
                    value={formData.title_nl}
                    onChange={(e) => setFormData({ ...formData, title_nl: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                    placeholder="Artikeltitel..."
                    data-testid="input-title-nl"
                  />
                  <textarea
                    value={formData.excerpt_nl}
                    onChange={(e) => setFormData({ ...formData, excerpt_nl: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] resize-none"
                    placeholder="Korte samenvatting..."
                  />
                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="flex items-center gap-1 p-2 bg-slate-50 border-b border-slate-200">
                      <button type="button" onClick={() => insertMarkdown('**')} className="p-2 hover:bg-slate-200 rounded-lg" title="Vet">
                        <Bold size={16} />
                      </button>
                      <button type="button" onClick={() => insertMarkdown('*')} className="p-2 hover:bg-slate-200 rounded-lg" title="Cursief">
                        <Italic size={16} />
                      </button>
                      <button type="button" onClick={() => setFormData({ ...formData, content_nl: formData.content_nl + '\n\n- Item 1\n- Item 2' })} className="p-2 hover:bg-slate-200 rounded-lg" title="Lijst">
                        <List size={16} />
                      </button>
                      <span className="ml-2 text-xs text-slate-400">Markdown ondersteund</span>
                    </div>
                    <textarea
                      value={formData.content_nl}
                      onChange={(e) => setFormData({ ...formData, content_nl: e.target.value })}
                      required
                      rows={10}
                      className="w-full px-4 py-3 resize-none focus:outline-none"
                      placeholder="Schrijf hier je artikel..."
                      data-testid="input-content-nl"
                    />
                  </div>
                </div>

                {/* Other languages */}
                {languages.slice(1).map((lang) => (
                  <div key={lang.code} className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedLangs(prev => ({ ...prev, [lang.code]: !prev[lang.code] }))}
                      className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 font-medium text-[#0f172a]">
                        <span className="text-xl">{lang.flag}</span>
                        {lang.name}
                        {formData[`title_${lang.code}`] && (
                          <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Vertaald</span>
                        )}
                      </div>
                      {expandedLangs[lang.code] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    <AnimatePresence>
                      {expandedLangs[lang.code] && (
                        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                          <div className="p-4 space-y-4">
                            <input type="text" value={formData[`title_${lang.code}`]} onChange={(e) => setFormData({ ...formData, [`title_${lang.code}`]: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200" placeholder={`Titel in ${lang.name}...`} />
                            <textarea value={formData[`excerpt_${lang.code}`]} onChange={(e) => setFormData({ ...formData, [`excerpt_${lang.code}`]: e.target.value })} rows={2} className="w-full px-4 py-3 rounded-xl border border-slate-200 resize-none" placeholder={`Samenvatting in ${lang.name}...`} />
                            <textarea value={formData[`content_${lang.code}`]} onChange={(e) => setFormData({ ...formData, [`content_${lang.code}`]: e.target.value })} rows={6} className="w-full px-4 py-3 rounded-xl border border-slate-200 resize-none" placeholder={`Content in ${lang.name}...`} />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}

                {/* Meta fields */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#0f172a] mb-2">
                      <Image size={16} className="inline mr-2" />Afbeelding
                    </label>
                    {formData.image_url && (
                      <div className="mb-3 relative rounded-xl overflow-hidden">
                        <img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover" />
                        <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600">
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setShowImageSearch(!showImageSearch)} className="flex-1 px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl hover:border-[#2563EB] hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-slate-600">
                        <Search size={18} />Zoek afbeelding
                      </button>
                      <label className="flex-1 px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl hover:border-green-500 hover:bg-green-50 transition-colors flex items-center justify-center gap-2 text-slate-600 cursor-pointer">
                        <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" />
                        {uploadingImage ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                        {uploadingImage ? 'Uploaden...' : 'Upload foto'}
                      </label>
                    </div>
                    <input type="url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 mt-2" placeholder="Of plak afbeelding URL..." />
                    
                    <AnimatePresence>
                      {showImageSearch && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                          <div className="flex gap-2 mb-4">
                            <input type="text" value={imageSearchQuery} onChange={(e) => setImageSearchQuery(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchImages())} className="flex-1 px-4 py-2 rounded-lg border border-slate-200" placeholder="Zoek afbeeldingen..." />
                            <button type="button" onClick={searchImages} disabled={searchingImages} className="px-4 py-2 bg-[#2563EB] text-white rounded-lg hover:bg-[#1D4ED8] disabled:opacity-50 flex items-center gap-2">
                              {searchingImages ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}Zoeken
                            </button>
                          </div>
                          {searchedImages.length > 0 && (
                            <div className="grid grid-cols-3 gap-3">
                              {searchedImages.map((img) => (
                                <button key={img.id} type="button" onClick={() => { setFormData(prev => ({ ...prev, image_url: img.url })); setShowImageSearch(false); }} className="relative group rounded-lg overflow-hidden aspect-video hover:ring-2 hover:ring-[#2563EB]">
                                  <img src={img.thumb_url} alt={img.alt} className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-2">Categorie</label>
                    <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200">
                      <option value="">Selecteer categorie</option>
                      {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-2">Auteur</label>
                    <input type="text" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200" placeholder="Auteur naam" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-2"><Tag size={16} className="inline mr-2" />Tags</label>
                    <div className="flex gap-2">
                      <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} className="flex-grow px-4 py-3 rounded-xl border border-slate-200" placeholder="Tag toevoegen..." />
                      <button type="button" onClick={addTag} className="px-4 py-3 bg-slate-200 rounded-xl hover:bg-slate-300">+</button>
                    </div>
                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.tags.map((tag) => (
                          <span key={tag} className="inline-flex items-center gap-1 bg-[#2563EB]/10 text-[#2563EB] px-3 py-1 rounded-full text-sm">
                            #{tag}
                            <button type="button" onClick={() => setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) })} className="hover:text-red-500">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Publish toggle */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })} className="w-5 h-5 rounded border-slate-300 text-[#2563EB] focus:ring-[#2563EB]" />
                    <span className="font-medium text-[#0f172a]">{formData.is_published ? 'Gepubliceerd' : 'Concept'}</span>
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="btn-accent flex items-center gap-2" data-testid="save-post-button">
                    <Save size={20} />{editingId ? 'Opslaan' : 'Artikel aanmaken'}
                  </button>
                  <button type="button" onClick={resetForm} className="px-6 py-3 bg-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-300">
                    Annuleren
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Posts List */}
        <div className="space-y-4">
          {posts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Edit2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#0f172a] mb-2">Nog geen artikelen</h3>
              <p className="text-[#475569]">Maak je eerste blog artikel aan.</p>
            </div>
          ) : (
            posts.map((post) => (
              <BlogPostCard
                key={post.id}
                post={post}
                isTranslating={translatingIds.has(post.id)}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTogglePublished={togglePublished}
                onTranslate={translatePost}
                formatDate={formatDate}
              />
            ))
          )}
        </div>
      </div>
    </main>
  );
};

export default BlogAdminPage;
